import { AfterViewInit, Component, OnDestroy, ViewChild } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Subscription } from "rxjs";
import LocalizationConstants from "src/app/constants/localization-constants";
import RouteNameConstants from "src/app/constants/route-name-constants";
import { User } from "src/app/models/User";
import { ModalService } from "src/app/services/modal.service";
import { environment } from "src/environments/environment";

import { ConfirmationDialogData } from "../../common/confirmation-dialog/confirmation-dialog-modal.component";
import { AuthService } from "./../../../services/auth.service";
import { UserService, WIMSServiceUser } from "./../../../services/user.service";

@Component({
  selector: "app-users",
  templateUrl: "./users.component.html",
  styleUrls: ["./users.component.scss"],
  standalone: false,
})
export class UsersComponent implements OnDestroy, AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  readonly routeNameConstants = RouteNameConstants;
  readonly localizationConstants = LocalizationConstants;

  private subscriptions: Subscription[] = [];

  faSpinner = faSpinner;
  dataSource = new MatTableDataSource<User>([]);
  isLoading: boolean;
  pageSize: number = 10;

  columns = [
    // TODO: implement custom sorting for firstName + lastName
    { key: "firstName", title: LocalizationConstants.USER_NAME },
    { key: "email", title: LocalizationConstants.USER_EMAIL },
    { key: "role", title: LocalizationConstants.USER_ROLE },
    { key: "companies", title: LocalizationConstants.USER_COMPANIES },
    { key: "createDate", title: LocalizationConstants.USER_CREATE_DATE },
  ];

  columnKeysAll = [...this.columns.map((x) => x.key), "action1", "action2"];

  constructor(
    private modalService: ModalService,
    public userService: UserService,
    private authService: AuthService,
  ) {
    this.getUsers();
  }

  ngAfterViewInit() {
    this.dataSource.filterPredicate = this.onQuickSearch;
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  getUsers() {
    this.isLoading = true;
    this.userService.getAll().subscribe(
      (users) => {
        if (!users) return;

        this.isLoading = false;
        this.dataSource.data = users;
      },
      () => (this.isLoading = false),
    );
  }

  onDeleteUser(user: User) {
    const resultObservable = this.modalService.openConfirmationModal(
      new ConfirmationDialogData(LocalizationConstants.USERS_REMOVE_DIALOG_TITLE, [
        LocalizationConstants.USERS_REMOVE_DIALOG_CONTENT,
      ]),
    );

    const subscription = resultObservable.subscribe((result) => {
      if (result) {
        this.deleteUser(user);
      }
    });

    this.subscriptions.push(subscription);
  }

  private deleteUser(user: User) {
    user.isItemLoading = true;
    this.userService.delete(user.userId).subscribe(() => {
      user.isItemLoading = false;
      this.dataSource.data = this.dataSource.data.filter((u) => u.userId != user.userId);
    });
  }

  trackByFn(item) {
    return item.name;
  }

  allowEdit(user: User) {
    if (user.isItemLoading) return false;
    if (user.email === WIMSServiceUser) return false;
    if (this.authService.user.userId === user.userId) return true;

    if (user.isAdminGroupProfile) {
      return this.authService.user?.isAdmin;
    }

    return true;
  }

  allowDelete(user: User) {
    if (user.isItemLoading) return false;
    if (user.email === WIMSServiceUser) return false;
    if (this.authService.user.userId === user.userId) return false;

    if (user.isAdminGroupProfile) {
      return this.authService.user?.isAdmin;
    }

    return true;
  }

  public onQuickSearch(user: User, quickSearch: string): boolean {
    if (!quickSearch) {
      return true;
    }
    const searchValue = quickSearch.toLowerCase();
    return (
      user.email?.toLowerCase().includes(searchValue) ||
      user.emailSubscriptions?.some((y) => y.email?.toLowerCase().includes(searchValue)) ||
      // TODO: Check and fix this line, this should always return false
      // @ts-expect-error - includes() expects a string as argument, not a function
      user.famcodes?.includes((y) => y?.toLowerCase().includes(searchValue)) ||
      (user.firstName + " " + user.lastName)?.toLowerCase().includes(searchValue) ||
      user.phoneNumber?.toLowerCase().includes(searchValue) ||
      user.role == searchValue ||
      user.companies?.some(
        (y) =>
          y?.name?.toLowerCase().includes(searchValue) ||
          y?.fullName?.toLowerCase().includes(searchValue),
      ) ||
      user.subscriptions?.some((y) => y?.name.toLowerCase().includes(searchValue)) ||
      user.userVendors?.some((y) => y?.vendorName.toLowerCase().includes(searchValue)) ||
      user.planningBoardRole?.pbRole?.toLowerCase().includes(searchValue)
    );
  }

  public createUserInAzure(): void {
    window.open(environment.azureNewUserCreationFormUrl);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x.unsubscribe());
  }
}
